import { create } from "zustand";
import type {
  PlatformPos,
  PosEstado,
  PosResumenDeLaCola,
} from "@/platform/types";
import {
  desenlacesDelLote,
  formatosDelLote,
  type EnvioDelLote,
  type PausaDelDrenador,
  type SaleResult,
} from "./cola";
import { useEstadoDeLaCaja } from "./estadoDeLaCaja";

/**
 * El drenador de la cola (trd-client#338 AC-B; contrato §2.2, slice 5 de
 * `OFFLINE-POS-RONDA-1.md`). En la v1 corre en la pantalla: en el mostrador
 * la app está abierta todo el día. El proceso de fondo en Rust queda para F4.
 *
 * - **Uno por terminal.** Sube en orden de `seq`, en lotes de hasta 20, siempre
 *   con la misma clave por venta, y una en «necesita atención» no frena a las
 *   demás.
 * - **Se dispara** al confirmar una venta, al volver la red, al abrir la app y
 *   por un timer.
 * - **Sin conexión no manda nada.** Con la sesión vencida, el negocio
 *   suspendido o sin permiso, se pausa: las ventas siguen pendientes.
 */

export const LOTE = 20;
/** El timer del drenador: retoma lo que quedó en backoff. */
export const CADA_MS = 10_000;
/** Una pausa se vuelve a probar sola después de esto. */
export const PAUSA_MS = 60_000;
/** Cuánto se espera la respuesta de un lote. */
export const TIEMPO_MAXIMO_DEL_LOTE_MS = 30_000;

export interface DependenciasDelDrenador {
  pos: Pick<PlatformPos, "takeBatch" | "reportBatch" | "queueSummary">;
  /** `POST` al BFF: `/pos/terminals/{id}/sales`. */
  enviar: (url: string, cuerpo: unknown) => Promise<{ data?: unknown }>;
  /** El refresco de sesión único de la app. */
  renovarSesion: () => Promise<void>;
  ahora?: () => number;
  /** Para los tests: sin timer. */
  sinTimer?: boolean;
}

/** Lo que la pantalla de pendientes cuenta del drenador. */
export const useAvisoDelDrenador = create<{
  aviso: string | null;
  pausa: PausaDelDrenador | null;
}>(() => ({ aviso: null, pausa: null }));

type Escucha = (resultado: SaleResult) => void;

interface Drenador {
  businessId: string;
  despertar: () => void;
  escuchar: (saleId: string, escucha: Escucha) => () => void;
  soltar: () => void;
}

let actual: Drenador | null = null;

function crearDrenador(
  businessId: string,
  deps: DependenciasDelDrenador
): Drenador {
  const ahora = deps.ahora ?? Date.now;
  const escuchas = new Map<string, Set<Escucha>>();
  let corriendo = false;
  let otraVuelta = false;
  let soltado = false;
  let pausa: { motivo: PausaDelDrenador; desde: number } | null = null;
  let ultimaRenovacion = -Infinity;

  const pausar = (motivo: PausaDelDrenador | null) => {
    pausa = motivo ? { motivo, desde: ahora() } : null;
    useAvisoDelDrenador.setState({ pausa: motivo });
  };

  const avisarResultados = (
    resultados: { saleId: string; respuesta?: unknown }[]
  ) => {
    for (const { saleId, respuesta } of resultados) {
      if (!respuesta) continue;
      escuchas
        .get(saleId)
        ?.forEach((escucha) => escucha(respuesta as SaleResult));
    }
  };

  const refrescarPendientes = async () => {
    try {
      const resumen: PosResumenDeLaCola = await deps.pos.queueSummary(
        businessId
      );
      useEstadoDeLaCaja.setState({ pendientes: resumen.pendientes });
    } catch {
      // El latido los vuelve a contar.
    }
  };

  const renovar = async () => {
    if (ahora() - ultimaRenovacion < PAUSA_MS) return;
    ultimaRenovacion = ahora();
    try {
      await deps.renovarSesion();
      pausar(null);
      void ciclo();
    } catch {
      // Queda pausado: «Iniciá sesión para subir las ventas».
    }
  };

  const ciclo = async (): Promise<void> => {
    if (soltado) return;
    if (corriendo) {
      otraVuelta = true;
      return;
    }
    corriendo = true;
    try {
      do {
        otraVuelta = false;
        if (pausa && ahora() - pausa.desde >= PAUSA_MS) pausar(null);
        if (pausa) break;
        if (useEstadoDeLaCaja.getState().conexion === "sin-conexion") break;

        for (;;) {
          const lote = await deps.pos.takeBatch({ businessId, limit: LOTE });
          if (!lote || lote.sales.length === 0) break;

          let envio: EnvioDelLote;
          try {
            const respuesta = await deps.enviar(
              `/pos/terminals/${encodeURIComponent(lote.terminalId)}/sales`,
              { installationId: lote.installationId, sales: lote.sales }
            );
            envio = { ok: true, cuerpo: respuesta?.data };
          } catch (error) {
            envio = { ok: false, error };
          }

          const {
            desenlaces,
            pausa: motivo,
            aviso,
          } = desenlacesDelLote(
            envio,
            lote.saleIds,
            formatosDelLote(lote.sales)
          );
          await deps.pos.reportBatch(desenlaces);
          avisarResultados(desenlaces);
          if (aviso) useAvisoDelDrenador.setState({ aviso });
          await refrescarPendientes();

          if (motivo) {
            pausar(motivo);
            if (motivo === "sesion") void renovar();
            break;
          }
          // El lote entero volvió a pendiente (red, 5xx): no se insiste ahora;
          // lo retoma el timer con el backoff.
          if (desenlaces.every((d) => d.estado === "pendiente")) break;
        }
      } while (otraVuelta && !pausa && !soltado);
    } catch {
      // El almacén no respondió: lo retoma el timer.
    } finally {
      corriendo = false;
    }
  };

  // Volvió la red, o el latido levantó lo que pausaba al drenador (la pausa
  // se llama como el bloqueo del latido). Si el latido nunca vio ese bloqueo,
  // la pausa se vuelve a probar sola a los `PAUSA_MS`.
  const dejarDeMirar = useEstadoDeLaCaja.subscribe(
    (estado: PosEstado, antes: PosEstado) => {
      if (soltado) return;
      const levantada =
        pausa !== null &&
        antes.bloqueo?.tipo === pausa.motivo &&
        estado.bloqueo?.tipo !== pausa.motivo;
      if (levantada) pausar(null);
      const volvioLaRed =
        antes.conexion === "sin-conexion" && estado.conexion !== "sin-conexion";
      if (levantada || volvioLaRed) void ciclo();
    }
  );

  const timer = deps.sinTimer ? null : setInterval(() => void ciclo(), CADA_MS);
  void ciclo();

  return {
    businessId,
    despertar: () => void ciclo(),
    escuchar: (saleId, escucha) => {
      const conjunto = escuchas.get(saleId) ?? new Set<Escucha>();
      conjunto.add(escucha);
      escuchas.set(saleId, conjunto);
      return () => {
        conjunto.delete(escucha);
        if (conjunto.size === 0) escuchas.delete(saleId);
      };
    },
    soltar: () => {
      soltado = true;
      if (timer) clearInterval(timer);
      dejarDeMirar();
      escuchas.clear();
    },
  };
}

/** Uno por terminal: con el mismo negocio no hace nada; sin sesión, se suelta. */
export function asegurarDrenador(
  businessId: string | null | undefined,
  deps: DependenciasDelDrenador
): void {
  if (actual && actual.businessId === businessId) return;
  actual?.soltar();
  actual = null;
  useAvisoDelDrenador.setState({ aviso: null, pausa: null });
  if (businessId) actual = crearDrenador(businessId, deps);
}

/** Hay algo nuevo para subir: una venta confirmada o un reintento a mano. */
export function despertarDrenador(): void {
  actual?.despertar();
}

/**
 * Espera el resultado de una venta hasta `hastaMs` (un instante, no una
 * duración). Devuelve `null` si no llegó: la venta sigue en la cola igual.
 */
export function esperarResultado(
  saleId: string,
  hastaMs: number,
  ahora: () => number = Date.now
): Promise<SaleResult | null> {
  const drenador = actual;
  const restante = hastaMs - ahora();
  if (!drenador || restante <= 0) return Promise.resolve(null);
  return new Promise((resolve) => {
    const dejar = drenador.escuchar(saleId, (resultado) => {
      clearTimeout(tiempo);
      dejar();
      resolve(resultado);
    });
    const tiempo = setTimeout(() => {
      dejar();
      resolve(null);
    }, restante);
  });
}

/** Para los tests: arranca de cero. */
export function reiniciarDrenadorParaTests() {
  actual?.soltar();
  actual = null;
  useAvisoDelDrenador.setState({ aviso: null, pausa: null }, true);
}
